/**
 * Signed-URL access for figure media.
 *
 * Figure `href`s point at a private CloudFront distribution
 * (`{baseUrl}/media/{filename}`) that only serves requests carrying a signed
 * `Policy`/`Signature`/`Key-Pair-Id` query string. That query string is scoped
 * to one submission + revision and obtained from a separate `media-access`
 * endpoint; it is the same for every file under that revision, and expires
 * after roughly an hour.
 */
export interface MediaAccessResponse {
  baseUrl: string;
  queryString: string;
  expiresAt: number;
}

export interface FetchMediaAccessParams {
  submissionId: string;
  version: string;
  revision: number;
  signal?: AbortSignal;
}

export async function fetchMediaAccess({
  submissionId,
  version,
  revision,
  signal,
}: FetchMediaAccessParams): Promise<MediaAccessResponse> {
  const apiBaseUrl = import.meta.env['VITE_MEDIA_ACCESS_API_BASE_URL'];
  const accessToken = import.meta.env['VITE_USR_MGMT_ACCESS_TOKEN'];

  // Build revision string in format: v{version}-r{revision}
  const revisionString = `v${version}-r${revision}`;
  const url = `${String(apiBaseUrl).replace(/\/+$/, '')}/v2/files/${submissionId}/revisions/${revisionString}/media-access`;

  const response = await fetch(url, {
    headers: { 'USR-MGMT-ACCESS-TOKEN': String(accessToken) },
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new Error(
      `media-access request failed: ${response.status} ${response.statusText}`
    );
  }

  return (await response.json()) as MediaAccessResponse;
}

const S3_MEDIA_FILENAME = /\/media\/([^/?#]+)$/;

/** Pulls the filename off an `s3://.../media/{filename}` figure href. */
export function extractMediaFilename(href: string): string | null {
  return S3_MEDIA_FILENAME.exec(href)?.[1] ?? null;
}

/**
 * Builds the signed CloudFront URL for an `s3://` figure href. Uses the
 * contract's own `submissionId`/`version`/`revision` (what media-access was actually
 * asked to sign) rather than any segments embedded in the href itself, and
 * `VITE_MEDIA_CDN_BASE_URL` in place of the s3 bucket.
 */
export function buildSignedMediaUrl(
  href: string,
  submissionId: string,
  version: string,
  revision: number,
  queryString: string
): string | null {
  const filename = extractMediaFilename(href);
  const base = import.meta.env['VITE_MEDIA_CDN_BASE_URL'];

  if (!filename || !base) return null;
  // Build revision string in format: v{version}-r{revision}
  const revisionString = `v${version}-r${revision}`;

  return `${String(base).replace(/\/+$/, '')}/${submissionId}/${revisionString}/media/${filename}?${queryString}`;
}
